import React, { useEffect, useState } from 'react';
import { Select, Tag } from 'antd';
import dayjs from 'dayjs';
import { apiRoot } from '../../services/apiInstances';
import { useHasRole } from '../auth/RoleGuard';
import { useTechAvailabilitySummary, TechAvailability } from '../../hooks/useTechAvailabilitySummary';

interface StaffUser {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

/** Maps an availability status to the tag color/text shown in the picker. */
export function badgeFor(a: TechAvailability): { color: string; text: string } {
  switch (a.status) {
    case 'free':
      return { color: 'green', text: 'Free' };
    case 'busy':
      return { color: 'orange', text: `Busy until ${formatTime(a.busy_until)}` };
    case 'booked_out':
      return { color: 'red', text: 'Booked out' };
    case 'off':
    default:
      return { color: 'default', text: 'Off today' };
  }
}

function formatTime(value: string | null): string {
  if (!value) return '';
  return dayjs(value).format('h:mm A');
}

export interface TechnicianSelectProps {
  /**
   * Current selection: the users.id FK when assigned via the picker, or a
   * legacy free-text name string (older WOs never stored the FK). AntD Form
   * injects this automatically when used inside a Form.Item.
   */
  value?: number | string | null;
  /**
   * Legacy free-text technician name — displayed as the current value when no
   * FK is set so pre-picker work orders still show who was assigned.
   */
  legacyName?: string | null;
  /**
   * Fires with BOTH the user id and display name so callers can persist
   * assigned_technician_id (drives labor cost in P&L) and assigned_technician
   * (legacy display column) together. Cleared → (null, null).
   */
  onChange?: (techId: number | null, techName: string | null) => void;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  placeholder?: string;
}

/**
 * Staff picker for work-order technician assignment. Loads active technicians
 * + owners (same role filtering as TechAssignmentEditor) and saves the users.id
 * FK — the column PnlService needs to cost labor — alongside the display name.
 */
const TechnicianSelect: React.FC<TechnicianSelectProps> = ({
  value,
  legacyName,
  onChange,
  disabled = false,
  size = 'middle',
  style,
  placeholder = 'Select technician',
}) => {
  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(false);
  const canSeeAvailability = useHasRole(['owner', 'advisor']);
  const { byTechId, load: loadAvailability } = useTechAvailabilitySummary();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [techniciansResponse, ownersResponse] = await Promise.all([
          apiRoot.get('/users', { params: { role: 'technician', status: 'active' } }),
          apiRoot.get('/users', { params: { role: 'owner', status: 'active' } }),
        ]);
        if (cancelled) return;
        const allUsers: StaffUser[] = [...techniciansResponse.data, ...ownersResponse.data].sort(
          (a, b) => {
            const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
            const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
            return nameA.localeCompare(nameB);
          }
        );
        setStaff(allUsers);
      } catch (error) {
        // Quietly degrade — the field still shows the current value; options
        // simply stay empty (no toast spam offline / in tests).
        console.warn('TechnicianSelect: failed to load staff users', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const fullName = (u: StaffUser) => `${u.first_name || ''} ${u.last_name || ''}`.trim();

  // A numeric value is the FK; otherwise fall back to the legacy name string
  // (AntD Select renders a raw string value as-is when it matches no option).
  const displayValue: number | string | undefined =
    typeof value === 'number'
      ? value
      : legacyName && legacyName.trim()
        ? legacyName
        : undefined;

  const handleChange = (v: number | string | undefined) => {
    if (v === undefined || v === null) {
      onChange?.(null, null);
      return;
    }
    if (typeof v === 'number') {
      const user = staff.find((u) => u.id === v);
      onChange?.(v, user ? fullName(user) : null);
    }
    // A string value can only appear programmatically (legacy display) — the
    // options all carry numeric ids, so user picks are always numbers.
  };

  return (
    <Select<number | string>
      value={displayValue}
      onChange={handleChange}
      options={staff.map((u) => ({ value: u.id, label: fullName(u) }))}
      loading={loading}
      disabled={disabled}
      allowClear
      showSearch
      filterOption={(input, option) =>
        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      placeholder={placeholder}
      size={size}
      style={{ width: '100%', ...style }}
      onOpenChange={
        canSeeAvailability
          ? (open) => {
              if (open) loadAvailability();
            }
          : undefined
      }
      optionRender={
        canSeeAvailability
          ? (option) => {
              const availability = byTechId[option.value as number];
              if (!availability) return <>{option.label}</>;
              const { color, text } = badgeFor(availability);
              return (
                <span>
                  {option.label} — <Tag color={color}>{text}</Tag>
                </span>
              );
            }
          : undefined
      }
    />
  );
};

export default TechnicianSelect;
